import type { ParsedMaterial, ParsedIndustrySkill, IndustrySkill } from '@/types';

/**
 * Parse materials CSV string from ItemBlueprint
 *
 * Format: "itemId,itemName,category,quantity,itemId,itemName,category,quantity,..."
 * Note: Item names may contain commas and be quoted
 *
 * Example:
 * "41000000000,Tritanium,Minerals,16951751,42001000003,\"Condensed Alloy\",\"Planet Solid\",28899"
 *
 * @param materialsStr - CSV string of materials
 * @returns Array of parsed materials
 */
export function parseMaterials(materialsStr: string | undefined): ParsedMaterial[] {
  // Handle null, undefined, or non-string values
  if (!materialsStr || typeof materialsStr !== 'string') {
    return [];
  }

  if (materialsStr.trim() === '') {
    return [];
  }

  try {
    // Split by comma but respect quoted strings
    // This regex handles quoted fields that may contain commas
    const parts = materialsStr.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g) || [];

    const materials: ParsedMaterial[] = [];

    // Process in groups of 4: itemId, itemName, category, quantity
    for (let i = 0; i + 3 < parts.length; i += 4) {
      const itemId = parts[i]?.replace(/"/g, '').trim();
      const itemName = parts[i + 1]?.replace(/"/g, '').trim();
      const category = parts[i + 2]?.replace(/"/g, '').trim();
      const quantityStr = parts[i + 3]?.replace(/"/g, '').trim();

      if (!itemId || !itemName || !category || !quantityStr) {
        continue; // Skip incomplete entries
      }

      const quantity = parseFloat(quantityStr);

      if (isNaN(quantity)) {
        console.warn(`Invalid quantity "${quantityStr}" for material ${itemName}`);
        continue;
      }

      materials.push({
        itemId,
        itemName,
        category,
        quantity,
      });
    }

    return materials;
  } catch (error) {
    console.error('Error parsing materials:', error, materialsStr);
    return [];
  }
}

/**
 * Parse skills CSV string from Blueprint
 *
 * Format: "Skill Name 1,Skill Name 2,Skill Name 3"
 *
 * Example: "Frigate Manufacture,Advanced Frigate Manufacture,Expert Frigate Manufacture"
 *
 * @param skillsStr - Comma-separated skill names
 * @returns Array of skill names
 */
export function parseSkills(skillsStr: string | undefined): string[] {
  if (!skillsStr || skillsStr.trim() === '') {
    return [];
  }

  return skillsStr
    .split(',')
    .map((skill) => skill.trim())
    .filter((skill) => skill.length > 0);
}

/**
 * Parse industry skill efficiency and time values
 *
 * Format:
 * - efficiency: "4,8,12,16,20" (percentages)
 * - time: "-0.05,-0.1,-0.15,-0.2,-0.25" (negative percentages)
 *
 * @param skill - IndustrySkill from API
 * @returns Parsed skill with numeric arrays
 */
export function parseIndustrySkill(skill: IndustrySkill): ParsedIndustrySkill {
  const efficiencyByLevel = skill.efficiency
    .split(',')
    .map((val) => parseFloat(val.trim()))
    .filter((val) => !isNaN(val));

  const timeByLevel = skill.time
    .split(',')
    .map((val) => parseFloat(val.trim()))
    .filter((val) => !isNaN(val));

  return {
    name: skill.name,
    efficiencyByLevel,
    timeByLevel,
  };
}

/**
 * Apply efficiency modifier to a base quantity
 *
 * Efficiency values are negative percentages (e.g., -0.1 for 10% reduction)
 * Formula: finalQuantity = baseQuantity × (1 + efficiency)
 * Always rounds UP to ensure you have enough materials
 *
 * @param baseQuantity - Base material quantity
 * @param efficiencyPercent - Efficiency modifier (negative percentage, e.g., -0.1)
 * @returns Adjusted quantity (rounded up)
 */
export function applyEfficiency(baseQuantity: number, efficiencyPercent: number): number {
  const multiplier = 1 + efficiencyPercent;
  return Math.ceil(baseQuantity * multiplier);
}

/**
 * Get the best (most negative) efficiency from multiple skills
 *
 * When multiple skills apply to a blueprint, only the best efficiency is used
 * (not cumulative)
 *
 * @param efficiencies - Array of efficiency values
 * @returns Best (most negative) efficiency, or 0 if no efficiencies provided
 */
export function getBestEfficiency(efficiencies: number[]): number {
  if (efficiencies.length === 0) {
    return 0;
  }

  // Most negative (best) efficiency
  return Math.min(...efficiencies);
}

/**
 * Validate material entry
 *
 * @param material - Parsed material to validate
 * @returns True if material is valid
 */
export function isValidMaterial(material: ParsedMaterial): boolean {
  return (
    material.itemId.length > 0 &&
    material.itemName.length > 0 &&
    material.category.length > 0 &&
    material.quantity > 0 &&
    !isNaN(material.quantity) &&
    isFinite(material.quantity)
  );
}

/**
 * Format large numbers with thousand separators
 *
 * @param num - Number to format
 * @returns Formatted string (e.g., "1,234,567")
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(num));
}

/**
 * Calculate the number of manufacturing runs needed
 *
 * Accounts for blueprint output quantity
 *
 * @param targetQuantity - Desired quantity
 * @param outputPerRun - Units produced per manufacturing run
 * @returns Number of runs needed (rounded up)
 */
export function calculateRuns(targetQuantity: number, outputPerRun: number): number {
  if (outputPerRun <= 0) {
    throw new Error('Output per run must be greater than 0');
  }
  return Math.ceil(targetQuantity / outputPerRun);
}

/**
 * Sort skills by progression: Base -> Advanced -> Expert
 * Groups skills by their base name (e.g., all "Frigate Manufacture" variants together)
 *
 * @param skillNames - Array of skill names to sort
 * @returns Sorted array of skill names
 */
export function sortSkillsByProgression(skillNames: string[]): string[] {
  // Helper to extract base skill name and tier
  const parseSkillName = (name: string): { base: string; tier: number } => {
    if (name.startsWith('Expert ')) {
      return { base: name.replace('Expert ', ''), tier: 3 };
    }
    if (name.startsWith('Advanced ')) {
      return { base: name.replace('Advanced ', ''), tier: 2 };
    }
    return { base: name, tier: 1 };
  };

  return [...skillNames].sort((a, b) => {
    const parsedA = parseSkillName(a);
    const parsedB = parseSkillName(b);

    // First sort by base skill name
    const baseCompare = parsedA.base.localeCompare(parsedB.base);
    if (baseCompare !== 0) {
      return baseCompare;
    }

    // If same base, sort by tier (base=1, advanced=2, expert=3)
    return parsedA.tier - parsedB.tier;
  });
}
